import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

/**
 * TrustScoreDetail Component
 * Displays detailed trust score breakdown with formula and critical deficiencies
 */
export const TrustScoreDetail = ({ trustScore }) => {
  const { t } = useTranslation(['gap-analysis']);

  if (!trustScore) {
    return (
      <Card>
        <CardContent>
          <Typography color="textSecondary">{t('gap-analysis:assessment.trustScore.noScore')}</Typography>
        </CardContent>
      </Card>
    );
  }

  const { totalActualPoints, totalPossiblePoints, percentage, criticalDeficiencies, domainScores } = trustScore;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('gap-analysis:assessment.trustScore.breakdown')}
        </Typography>

        {/* Score Formula */}
        <Box mb={3}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {t('gap-analysis:assessment.trustScore.formula')}
          </Typography>
          <Box p={2} bgcolor="grey.100" borderRadius={1}>
            <Typography variant="body1" fontFamily="monospace">
              Trust Score = (Actual Points ÷ Possible Points) × 100
            </Typography>
            <Typography variant="body1" fontFamily="monospace" mt={1}>
              = ({totalActualPoints} ÷ {totalPossiblePoints}) × 100
            </Typography>
            <Typography variant="h6" fontFamily="monospace" mt={1} color="primary">
              = {percentage}%
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Domain Scores */}
        {domainScores && domainScores.length > 0 && (
          <Box mb={3}>
            <Typography variant="subtitle2" gutterBottom>
              {t('gap-analysis:assessment.trustScore.performanceByDomain')}
            </Typography>
            {domainScores.map((domain, idx) => (
              <Box key={idx} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2">{domain.domain}</Typography>
                <Typography variant="body2" fontWeight="bold" color={domain.percentage >= 90 ? 'success.main' : domain.percentage >= 70 ? 'warning.main' : 'error.main'}>
                  {domain.percentage.toFixed(1)}%
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Critical Deficiencies */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('gap-analysis:assessment.trustScore.criticalDeficiencies')} ({criticalDeficiencies?.length || 0})
          </Typography>

          {!criticalDeficiencies || criticalDeficiencies.length === 0 ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              <Typography variant="body2">
                {t('gap-analysis:assessment.trustScore.noCriticalDeficiencies')}
              </Typography>
            </Alert>
          ) : (
            <Box mt={1}>
              {criticalDeficiencies.map((deficiency, idx) => (
                <Accordion key={idx} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={1} width="100%">
                      <WarningIcon color="error" fontSize="small" />
                      <Typography variant="body2" fontWeight="medium">
                        {deficiency.controlDescription}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Domain: {deficiency.domain}
                      </Typography>
                      <Box mt={1} display="flex" gap={2}>
                        <Box>
                          <Typography variant="caption" color="textSecondary">{t('gap-analysis:assessment.trustScore.targetMaturity')}</Typography>
                          <Typography variant="body2" fontWeight="bold">L{deficiency.targetMaturity}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="textSecondary">{t('gap-analysis:assessment.trustScore.vendorMaturity')}</Typography>
                          <Typography variant="body2" fontWeight="bold">L{deficiency.vendorMaturity}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="textSecondary">{t('gap-analysis:assessment.trustScore.gap')}</Typography>
                          <Typography variant="body2" fontWeight="bold" color="error">
                            {deficiency.gap} levels
                          </Typography>
                        </Box>
                      </Box>
                      {deficiency.reason && (
                        <Box mt={1}>
                          <Typography variant="caption" color="textSecondary">{t('gap-analysis:assessment.trustScore.reason')}</Typography>
                          <Typography variant="body2">{deficiency.reason}</Typography>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
